import { bsvAmountToSats, normalizeDecimalAmount } from 'utils/bsvAmountInput'

// Shared parser/serializer for the !pay action's inline prompt ("@user 2000 sats").
// It is the single source of truth used in BOTH sync directions (token pill <-> pay card)
// and at submit, so the pill text, the card fields, and the send all agree.

const BITCOIN_UNITS = ['btc', 'bitcoin', 'bsv', 'bitcoinsv']

export interface ParsedPay {
    recipient: string
    amount: number
    unit: 'sats' | 'bsv'
    amountSats: number
}

export interface PayActionCommandInput {
    recipient: string
    rawAmount: string
    unit?: string
    amountSats?: number
}

/** Extract complete !pay commands from current bare mentions and legacy bracketed mentions. */
export function parsePayActionCommands(content: string): PayActionCommandInput[] {
    // An explicit unit ends the payment arguments; otherwise require "for" before a
    // trailing note so an unknown/misspelled unit cannot silently become a sats payment.
    // Leave the note unconsumed so later !pay commands are still extracted and validated.
    const commandRegex =
        /(?:!pay\s+|;\s*)(?:@\[([^\]]+)\]|@([^\s\]]+))\s+([\d.,]+)(?:\s+(satoshis|satoshi|sats|sat|bitcoinsv|bitcoin|btc|bsv)(?=$|\s|[.,;!?](?:\s|$))|(?=$|\s*$|\s+for(?:\s|$)|\s+(?:and\s+)?!pay\b|[.,;!?](?:\s|$)))/gi

    return [...content.matchAll(commandRegex)].map(match => ({
        recipient: match[1] || match[2],
        rawAmount: match[3],
        unit: match[4],
    }))
}

/**
 * Parse a pay pill's prompt content (NO "!pay" prefix), e.g. "@demo_alice 2000 sats" or
 * "@[demo alice] 0.00002 bsv", optionally followed by a note, into structured recipient/amount.
 * Notes without an explicit unit must start with "for". Returns null when the
 * prompt isn't a complete, valid payment yet (so callers can leave the card empty / block
 * submit). Mirrors the guards of the original submit-time pay parser: comma amounts are
 * rejected as locale-ambiguous, and the amount must resolve to a positive whole number of sats.
 */
export function parsePayPrompt(promptContent: string | null | undefined): ParsedPay | null {
    if (!promptContent || typeof promptContent !== 'string') return null
    const text = promptContent.trim()
    // One card represents one payment. Never swallow another command as part of its note.
    if (/!pay\s+@/i.test(text)) return null
    // Recipient: "@[name with spaces]" (bracketed) or "@name" (bare). Then amount, optional unit.
    const m = text.match(
        /^(?:@?\[([^\]]+)\]|@?([^\s\]]+))\s+([\d.,]+)(?:\s+(satoshis|satoshi|sats|sat|bitcoinsv|bitcoin|btc|bsv)(?:\s+[\s\S]*)?|\s+for(?:\s+[\s\S]*)?)?\s*$/i,
    )
    if (!m) return null

    const recipient = (m[1] || m[2] || '').replace(/^@+/, '').trim()
    if (!recipient) return null

    const rawAmount = m[3]
    // Reject comma amounts: "1,5" is locale-ambiguous (period=decimal is required).
    if (rawAmount.includes(',')) return null

    const amount = Number(rawAmount)
    if (!Number.isFinite(amount) || amount <= 0) return null

    const lowerUnit = (m[4] || 'sats').toLowerCase()
    const isBtc = BITCOIN_UNITS.includes(lowerUnit)
    const amountSats = bsvAmountToSats(rawAmount, isBtc ? 'bsv' : 'sats')
    if (amountSats === '' || amountSats <= 0) return null

    return { recipient, amount, unit: isBtc ? 'bsv' : 'sats', amountSats }
}

export interface PartialPay {
    recipient: string
    amount: number | null
    unit: 'sats' | 'bsv'
    amountSats: number | null // null until recipient+amount form a valid, sendable payment
}

/**
 * Tolerant parse of a pay pill prompt into structured fields, keeping partial/incomplete input
 * (recipient only, amount only) instead of discarding it. This is what settings.pay stores, so
 * the pay card can render the in-progress state and the card<->pill echo guard can converge.
 * amountSats is populated only when the payment is fully valid (see parsePayPrompt).
 */
export function parsePayPromptPartial(promptContent: string | null | undefined): PartialPay {
    const full = parsePayPrompt(promptContent)
    if (full) {
        return {
            recipient: full.recipient,
            amount: full.amount,
            unit: full.unit,
            amountSats: full.amountSats,
        }
    }
    const text = (promptContent || '').trim()
    const rm = text.match(/^(?:@?\[([^\]]+)\]|@?([^\s\]]+))/)
    const am = text.match(/[\s\]]([\d.]+)/) || (rm ? null : text.match(/^([\d.]+)/))
    const um = text.match(/\b(sat|sats|satoshi|satoshis|btc|bitcoin|bsv|bitcoinsv)\b/i)
    return {
        recipient: (rm?.[1] || rm?.[2] || '').replace(/^@+/, ''),
        amount: am?.[1] && !am[1].includes(',') ? Number(am[1]) : null,
        unit: um && /^(btc|bitcoin|bsv|bitcoinsv)$/i.test(um[1]) ? 'bsv' : 'sats',
        amountSats: null,
    }
}

/**
 * Build a structured PartialPay straight from card fields, WITHOUT round-tripping through the
 * pill text (formatPayPrompt -> parse would be lossy: float->string, unit re-inference). This is
 * how the pay card writes the authoritative settings.pay onto the action-request JSON. amountSats
 * uses the same guards as parsePayPrompt (positive whole sats; toSats for bsv) so a card-built pay
 * and a pill-parsed pay agree and gate submit identically.
 */
export function normalizePay(fields: {
    recipient?: string | null
    amount?: number | null
    unit?: string | null
}): PartialPay {
    const recipient = String(fields.recipient || '')
        .replace(/^@+/, '')
        .trim()
    const unit: 'sats' | 'bsv' = fields.unit === 'bsv' ? 'bsv' : 'sats'
    const amount =
        fields.amount != null && Number.isFinite(fields.amount) ? Number(fields.amount) : null
    let amountSats: number | null = null
    if (amount != null && amount > 0) {
        const sats = bsvAmountToSats(amount, unit)
        if (sats !== '' && sats > 0) amountSats = sats
    }
    return { recipient, amount, unit, amountSats }
}

/** Serialize structured pay fields back into the canonical pill prompt text. */
export function formatPayPrompt(fields: {
    recipient?: string | null
    amount?: number | null
    unit?: string | null
}): string {
    const recipient = String(fields.recipient || '')
        .replace(/^@+/, '')
        .trim()
    if (!recipient) return ''
    const amountStr =
        fields.amount != null && Number.isFinite(fields.amount)
            ? normalizeDecimalAmount(fields.amount)
            : ''
    const unit = fields.unit || 'sats'
    const mention = /\s/.test(recipient) ? `@[${recipient}]` : `@${recipient}`
    return `${mention} ${amountStr} ${unit}`.replace(/\s+/g, ' ').trim()
}

export interface PayGroup {
    payments: PartialPay[]
}

/** Read both current recipient rows and previously saved single-recipient drafts. */
export function payRows(pay: PayGroup | PartialPay | null | undefined): PartialPay[] {
    if (!pay) return [parsePayPromptPartial('')]
    return 'payments' in pay ? pay.payments : [pay]
}

/** Semicolons separate recipient rows inside one pay card's inline prompt. */
export function parsePayGroup(prompt: string | null | undefined): PayGroup {
    return { payments: (prompt || '').split(/;\s*(?=@|$)/).map(parsePayPromptPartial) }
}

export function formatPayGroup(pay: PayGroup): string {
    return pay.payments.map(formatPayPrompt).join('; ')
}

/** Never drop incomplete rows or extra typed commands when structured cards are present. */
export function paySubmissionInputs(
    content: string,
    cards: (PayGroup | PartialPay | null | undefined)[],
) {
    const commandCount = (content.match(/!pay\b/gi) || []).length
    if (cards.length && commandCount > cards.length) {
        return { matches: [], error: 'mixed' as const }
    }
    if (!cards.length) {
        const matches = parsePayActionCommands(content)
        const rowCount = commandCount + (content.match(/;\s*@/g) || []).length
        return { matches, error: rowCount > matches.length ? ('invalid' as const) : null }
    }
    const rows = cards.flatMap(payRows)
    if (commandCount + (content.match(/;\s*@/g) || []).length > rows.length) {
        return { matches: [], error: 'mixed' as const }
    }
    if (
        !rows.length ||
        rows.some(
            pay =>
                !pay.recipient ||
                !Number.isSafeInteger(pay.amountSats) ||
                normalizePay(pay).amountSats !== pay.amountSats ||
                (pay.amountSats || 0) <= 0,
        )
    ) {
        return { matches: [], error: 'invalid' as const }
    }
    return {
        matches: rows.map(pay => ({
            recipient: pay.recipient,
            rawAmount: normalizeDecimalAmount(pay.amount!),
            unit: pay.unit,
            amountSats: pay.amountSats!,
        })),
        error: null,
    }
}
